import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface RenameRoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  currentEntryCode?: string | null;
  currentEntryNotes?: string | null;
  onRename: (newName: string, entryCode?: string | null, entryNotes?: string | null) => void;
}

const RenameRoomDialog = ({
  open,
  onOpenChange,
  currentName,
  currentEntryCode = "",
  currentEntryNotes = "",
  onRename,
}: RenameRoomDialogProps) => {
  const [newName, setNewName] = useState(currentName);
  const [entryCode, setEntryCode] = useState(currentEntryCode ?? "");
  const [entryNotes, setEntryNotes] = useState(currentEntryNotes ?? "");

  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setEntryCode(currentEntryCode ?? "");
      setEntryNotes(currentEntryNotes ?? "");
    }
  }, [open, currentName, currentEntryCode, currentEntryNotes]);

  const handleSubmit = () => {
    if (newName.trim()) {
      onRename(
        newName.trim(),
        entryCode.trim() || undefined,
        entryNotes.trim() || undefined
      );
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Room</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="room-name">Room Name</Label>
            <Input
              id="room-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Kitchen, Living Room"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>Is there a code to enter this room?</Label>
            <p className="text-sm text-muted-foreground">
              Code or notes for the cleaner (e.g. lockbox code, key location).
            </p>
            <Input
              value={entryCode}
              onChange={(e) => setEntryCode(e.target.value)}
              placeholder="e.g., 1234 or key under mat"
            />
            <Textarea
              value={entryNotes}
              onChange={(e) => setEntryNotes(e.target.value)}
              placeholder="Additional notes for the cleaner..."
              rows={2}
              className="resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!newName.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenameRoomDialog;
